/* Reformly Home — explore cards.

   One state (which card is open, or none) driving two mechanics the
   stylesheet owns:

     desktop  the row gets .has-open and the card .is-open; flex animates
     mobile   the card gets .is-open; media height and grid-template-rows
              animate

   So this file only toggles classes and manages focus and a11y state. The
   geometry, the curves and the staggered entrance are all CSS — see
   assets/reformly-home-explore.css.

   Mobile and desktop are NOT separate code paths here on purpose: the export
   gates its behaviour on `exMob = w < 900`, but the only thing that actually
   differs is which properties animate, and CSS already knows the viewport.
   Branching in JS would mean re-deriving the breakpoint and keeping it in
   sync with the stylesheet.
*/
(function () {
  var rows = document.querySelectorAll('[data-rhm-ex]');
  if (!rows.length) return;

  Array.prototype.forEach.call(rows, function (row) {
    var cards = row.querySelectorAll('[data-rhm-ex-card]');
    if (!cards.length) return;

    var open = null;

    function close() {
      if (open === null) return;
      var card = cards[open];
      card.classList.remove('is-open');
      var hit = card.querySelector('[data-rhm-ex-open]');
      if (hit) hit.setAttribute('aria-expanded', 'false');
      row.classList.remove('has-open');
      open = null;
    }

    function openCard(i) {
      if (open === i) return;
      close();
      var card = cards[i];
      card.classList.add('is-open');
      row.classList.add('has-open');
      var hit = card.querySelector('[data-rhm-ex-open]');
      if (hit) hit.setAttribute('aria-expanded', 'true');
      open = i;

      /* The trigger is display: none once open at desktop, so focus would be
         lost to the document. Move it to the close button, which is the only
         control left on the card. */
      var btn = card.querySelector('[data-rhm-ex-close]');
      if (btn && document.activeElement === hit) btn.focus();

      /* On the mobile rail the opened card should be the one in view —
         tapping the peeking neighbour otherwise expands something half off
         screen. Guarded on scrollWidth so it is a no-op at desktop, where
         the row does not scroll. */
      if (row.scrollWidth > row.clientWidth + 1) {
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    }

    Array.prototype.forEach.call(cards, function (card, i) {
      var hit = card.querySelector('[data-rhm-ex-open]');
      var btn = card.querySelector('[data-rhm-ex-close]');

      if (hit) {
        hit.addEventListener('click', function () { openCard(i); });
      }

      if (btn) {
        btn.addEventListener('click', function () {
          close();
          /* Focus back to the trigger it came from, not to the body. */
          if (hit) hit.focus();
        });
      }
    });

    /* Escape closes, which is the expectation for anything that expands over
       its neighbours. */
    row.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || open === null) return;
      var hit = cards[open].querySelector('[data-rhm-ex-open]');
      close();
      if (hit) hit.focus();
    });

    /* ---------- Rail controls (phone) ----------

       The mobile export pages this rail with a progress line and two arrows.
       The rail itself is a real scroll container with scroll-snap, so paging
       is a scrollTo rather than a transform — the visitor can still swipe, and
       both paths land on the same snap points.

       The index is DERIVED from scrollLeft rather than tracked in a variable.
       A tracked index desynchronises the moment someone swipes instead of
       tapping, and then the arrows jump. Reading the scroll position means the
       swipe and the buttons share one source of truth.

       Progress is idx / (n - 1), matching the export's `exProgW`: empty on the
       first card, full on the last. */
    var nav = row.parentElement.querySelector('[data-rhm-ex-nav]');
    if (nav && cards.length > 1) {
      var fill = nav.querySelector('[data-rhm-ex-fill]');
      var prev = nav.querySelector('[data-rhm-ex-prev]');
      var next = nav.querySelector('[data-rhm-ex-next]');

      var step = function () {
        /* Measured, not assumed: the card is a percentage width plus a gap, and
           both change with the viewport. Two cards give the real pitch; one
           falls back to the card's own width. */
        if (cards.length < 2) return cards[0].offsetWidth;
        return cards[1].offsetLeft - cards[0].offsetLeft;
      };

      var indexOf = function () {
        var s = step();
        if (!s) return 0;
        return Math.round(row.scrollLeft / s);
      };

      var paint = function () {
        var i = indexOf();
        var last = cards.length - 1;
        if (fill) fill.style.width = (i / last) * 100 + '%';
        if (prev) prev.setAttribute('aria-disabled', i <= 0 ? 'true' : 'false');
        if (next) next.setAttribute('aria-disabled', i >= last ? 'true' : 'false');
      };

      var go = function (delta) {
        var i = Math.max(0, Math.min(cards.length - 1, indexOf() + delta));
        /* scrollLeft, not scrollIntoView: the latter also scrolls the PAGE
           vertically to bring the rail into view, which yanks the viewport
           when the arrows are tapped near the section edge. */
        row.scrollTo({ left: i * step(), behavior: 'smooth' });
      };

      if (prev) prev.addEventListener('click', function () { go(-1); });
      if (next) next.addEventListener('click', function () { go(1); });

      /* rAF-throttled: scroll fires far more often than the 1px bar can show. */
      var queued = false;
      row.addEventListener('scroll', function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () { queued = false; paint(); });
      }, { passive: true });

      window.addEventListener('resize', paint);
      paint();
    }

    /* Crossing the breakpoint while a card is open leaves the other
       mechanic mid-animation — the mobile body would be expanded inside a
       desktop overlay. Closing is the honest reset. */
    if (window.matchMedia) {
      var mq = window.matchMedia('(min-width: 900px)');
      var onChange = function () { close(); };
      if (mq.addEventListener) {
        mq.addEventListener('change', onChange);
      } else if (mq.addListener) {
        mq.addListener(onChange);
      }
    }
  });
})();
